import { z } from 'zod'

/**
 * Bot kiritmalari va API body'lari uchun Zod sxemalar. Barcha funksiyalar toza
 * (pure) — I/O yo'q, faqat kirish qiymatini tekshiradi yoki normallashtiradi.
 */

/** Telefon raqami — `+998XXXXXXXXX` formatida bo'lishi shart. */
export const phoneSchema = z
  .string()
  .regex(/^\+998\d{9}$/, "Raqamni +998901234567 ko'rinishida yozing")

/** Telefon raqamini turli formatlardan (bo'sh joy, tire, qavs) tozalab, `+998...` ga keltiradi. */
export function normalizePhone(input: string): string | null {
  if (typeof input !== 'string') return null
  const stripped = input.replace(/[\s\-()]/g, '')
  if (stripped.length === 0) return null

  let normalized: string
  if (stripped.startsWith('+998')) {
    normalized = stripped
  } else if (stripped.startsWith('998')) {
    normalized = `+${stripped}`
  } else if (/^\d{9}$/.test(stripped)) {
    normalized = `+998${stripped}`
  } else {
    return null
  }

  return phoneSchema.safeParse(normalized).success ? normalized : null
}

/** Instagram username — `@` siz, faqat kichik harf, raqam, nuqta va pastki chiziq, 1-30 belgi. */
export const instagramUsernameSchema = z
  .string()
  .min(1, 'Instagram username kiritilishi shart')
  .max(30, 'Instagram username juda uzun')
  .regex(
    /^[a-z0-9._]+$/,
    "Instagram username faqat kichik harf, raqam, '.' va '_' dan iborat bo'lishi kerak",
  )

/** Instagram username'ni `@` va profil havolasidan ajratib, kichik harfga o'girib normallaydi. */
export function normalizeInstagramUsername(input: string): string | null {
  if (typeof input !== 'string') return null
  let value = input.trim()
  if (value.length === 0) return null

  const urlMatch = value.match(/instagram\.com\/([^/?#\s]+)/i)
  if (urlMatch) {
    value = urlMatch[1] ?? ''
  }

  value = value.trim()
  if (value.startsWith('@')) {
    value = value.slice(1)
  }
  value = value.toLowerCase()

  const result = instagramUsernameSchema.safeParse(value)
  return result.success ? result.data : null
}

/** Obunachilar soni — minimal chegaradan kam bo'lmasligi kerak. */
export const followersSchema = z
  .number()
  .int("Obunachilar soni butun son bo'lishi kerak")
  .min(1000, "Obunachilar soni kamida 1000 bo'lishi kerak")

/** To'liq ism-familiya. */
export const fullNameSchema = z
  .string()
  .trim()
  .min(3, "Ism-familiya kamida 3 belgidan iborat bo'lishi kerak")
  .max(100, 'Ism-familiya juda uzun')

/** Karta raqami — bo'sh joylarni olib tashlagandan keyin aynan 16 raqam bo'lishi kerak. */
export const cardNumberSchema = z
  .string()
  .transform((v) => v.replace(/\s+/g, ''))
  .pipe(z.string().regex(/^\d{16}$/, "Karta raqami 16 xonali bo'lishi kerak"))

/** Karta raqamidan bo'sh joylarni olib tashlab, 16 xonali ekanligini tekshiradi. */
export function normalizeCardNumber(input: string): string | null {
  if (typeof input !== 'string') return null
  const stripped = input.replace(/\s+/g, '')
  return /^\d{16}$/.test(stripped) ? stripped : null
}

/** Karta egasining ismi (bank kartasida yozilgandek). */
export const cardHolderSchema = z
  .string()
  .trim()
  .min(3, "Karta egasi ismi kamida 3 belgidan iborat bo'lishi kerak")
  .max(100, 'Karta egasi ismi juda uzun')

/** Prosmotrlar soni — manfiy bo'lmasligi va oqilona chegaradan oshmasligi kerak. */
export const viewsSchema = z
  .number()
  .int("Prosmotrlar soni butun son bo'lishi kerak")
  .min(0, "Prosmotrlar soni manfiy bo'lishi mumkin emas")
  .max(100_000_000, 'Prosmotrlar soni juda katta')

/**
 * Instagram post/reel havolasidan shortcode'ni ajratib oladi. `reel`, `reels`,
 * `p`, `tv` yo'llarini, `www.`/`m.` prefiksini, protokol va so'roq qatorini qo'llab-quvvatlaydi.
 *
 * XAVFSIZLIK: domen ATAYLAB qat'iy bog'langan. Oldingi versiya oddiy qism-satr
 * qidiruvi edi va `evil.com/?u=instagram.com/reel/XXXXX` kabi havolani qabul qilardi —
 * bunday havola bazaga yozilib, keyin admin uni panelda bosgan bo'lardi.
 * Shuningdek shortcode chegarasi tekshiriladi: 20 belgidan uzun kod endi
 * jimgina qirqilmaydi, balki rad etiladi.
 */
export function extractShortcode(url: string): string | null {
  if (typeof url !== 'string') return null

  const trimmed = url.trim()
  // Domen boshi: satr boshi, bo'shliq yoki protokoldan keyin kelishi shart —
  // shunda "fakeinstagram.com" yoki so'roq qatoridagi soxta domen o'tmaydi.
  const pattern =
    /(?:^|\s|\/\/)(?:[a-z0-9-]+\.)*instagram\.com\/(?:reels?|p|tv)\/([A-Za-z0-9_-]{5,20})(?![A-Za-z0-9_-])/i

  const match = pattern.exec(trimmed)
  return match?.[1] ?? null
}

/** Instagram post/reel havolasi — shortcode ajratib bo'ladigan formatda bo'lishi kerak. */
export const instagramUrlSchema = z
  .string()
  .trim()
  .refine((v) => extractShortcode(v) !== null, {
    message: "Instagram havolasi noto'g'ri formatda",
  })
